<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <!-- Header Section -->
    <TopNavbar />

    <div class="flex-1 overflow-y-auto pb-[100px]">
      <div class="flex w-full items-center justify-between px-4 py-4">
        <a @click="goBack" class="text-prime-golden text-xl flex gap-2 items-center">
          <img class="w-6 h-6" src="@/assets/images/left-chevron.svg" alt="Back Button" />
        </a>
        <span class="text-xl ml-4"> My Account</span>
        <button @click="editAccount" class="text-prime-golden text-sm text-md">
          <i class="fas fa-pencil-alt mr-1"></i>
          <span class="underline underline-offset-4">edit</span>
        </button>
      </div>

      <!-- Account Info -->
      <div
        :style="{ opacity: isFetching ? '40%' : '100%' }"
        class="flex-1 overflow-y-auto p-4"
      >
        <div class="flex flex-col">
          <div class="flex flex-col items-center mb-10">
            <div
              class="w-20 h-20 rounded-full flex items-center mb-4 justify-center bg-light-grey-translucent-dark border border-light-grey-translucent-dark overflow-hidden"
            >
              <template v-if="account.profile_image_url">
                <img
                  :src="account.profile_image_url"
                  alt="Profile Image"
                  class="w-full h-full object-cover rounded-full border"
                />
              </template>
              <template v-else>
                <i
                  class="fa-solid fa-user text-6xl text-pitch-black -mb-[1px] mt-auto"
                ></i>
              </template>
            </div>

            <h2 class="text-3xl mb-2">
              {{ account.first_name }} {{ account.last_name }}
            </h2>
          </div>
          <!-- Account Information Details -->
          <div class="text-left px-1">
            <div class="mb-6">
              <div class="flex justify-between items-center">
                <h3 class="font-semibold">Email Address</h3>
              </div>
              <p class="text-sm py-2">{{ account.email }}</p>
            </div>

            <template v-if="account.authorized_email">
              <div class="mb-6">
                <div class="flex justify-start gap-2 items-center">
                  <h3 class="font-semibold">Authorized Email Address</h3>
                  <button @click="goToLinkEmailPage" class="w-4 h-4">
                    <img src="../../assets/images/add-icon.svg" alt="" />
                  </button>
                </div>
                <p class="text-sm py-2">{{ account.authorized_email }}</p>
              </div>
            </template>
            <div class="mb-6">
              <h3 class="font-semibold">Phone Numbers</h3>
              <div class="flex gap-8 text-sm mt-2">
                <div class="text-left">
                  <h3 class="pb-2">Personal</h3>
                  <template v-for="project in account.projects" :key="project.id">
                    <div class="flex items-center py-2">
                      <a
                        @click="goToProjectPage(project.id)"
                        class="text-prime-golden underline flex items-center"
                      >
                        <p class=" ">{{ project.address_1 }}</p>
                      </a>
                    </div>
                  </template>
                </div>
                <div class="text-left">
                  <h3 class="pb-2">{{ account.phone_number }}</h3>
                  <template v-for="project in account.projects" :key="project.id">
                    <div class="flex items-center py-2 justify-start">
                      <p class="text-left">{{ project.project_manager_twilio_number }}</p>
                      <button
                        @click="copyToClipboard(project.project_manager_twilio_number)"
                        class="ml-2"
                      >
                        <i class="fa-regular fa-copy text-prime-golden text-xl"></i>
                      </button>
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <div class="mb-6">
              <h3 class="font-semibold">Company</h3>
              <p class="text-sm py-2">{{ account.company }}</p>
            </div>
            <div class="mb-6">
              <h3 class="font-semibold">Account Type</h3>
              <p class="text-sm py-2">{{ account.account_type }}</p>
            </div>

            <!--<div class="mb-6">
              <div class="flex items-center gap-2">
                <h3 class="font-semibold">My Projects</h3>
              </div>
              <ul class="mt-2 space-y-2">
                <li v-for="project in account.projects" :key="project.id">
                  <a
                    :href="project.link"
                    target="_blank"
                    class="text-prime-golden flex items-center"
                  >
                    <span class="underline underline-offset-4">{{ project.name }}</span>
                    <i class="fas fa-external-link-alt ml-2"></i>
                  </a>
                </li>
              </ul>
            </div>-->
          </div>

          <!-- Update Password Button -->
          <!--<div class="flex justify-start my-4">
            <button
              @click="updatePassword"
              class="bg-light-grey-translucent-700 text-xs px-2 py-1 rounded-lg text-total-white"
            >
              Update My Password
            </button>
          </div>-->
        </div>
        <template v-if="account?.account_type === 'admin'">
          <div class="mt-8 flex items-center gap-2">
            <button
              @click="showWipeModal = true"
              class="bg-deep-rust text-white flex gap-2 items-center px-3 text-sm py-2 rounded-lg hover:bg-red-700 transition"
            >
              Wipe DB
            </button>
            <p class="text-xs">
              (Total Projects: <strong>{{ totalProjects }}</strong
              >)
            </p>
          </div>
        </template>
      </div>
    </div>

    <!-- User Drawer -->
    <UserDrawer
      :isOpen="isDrawerOpen"
      :userPicture="user.profile_image_url"
      :username="user.username"
      :first_name="user.first_name"
      :last_name="user.last_name"
      :email="user.email"
      :role="user.role"
      @close="toggleDrawer"
      @logout="handleLogout"
    />
    <WipeDBModal
      :isOpen="showWipeModal"
      @confirmWipe="confirmWipeDB"
      @cancelWipe="showWipeModal = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import UserDrawer from "@/components/UserDrawer.vue";
import { useUserStore } from "@/stores/user";
import { useAuthStore } from "@/stores/auth";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import TopNavbar from "@/components/TopNavbar.vue";
import { useActivationStore } from "@/stores/activation";
import WipeDBModal from "@/components/WipeDBModal.vue";

// User and account data
const userStore = useUserStore();
const authStore = useAuthStore();
const activationStore = useActivationStore();
const router = useRouter();
const route = useRoute();

const toast = useToast();

const isFetching = ref(false);
const totalProjects = ref(0);
const showWipeModal = ref(false);

// Account data reference
const account = ref({});

// User drawer state
const isDrawerOpen = ref(false);

// Computed property for user profile
const user = computed(
  () =>
    userStore.profile || {
      picture: "/assets/images/profile.png",
      username: "User",
      email: "user@example.com",
    }
);

// Fetch user profile on component mount
onMounted(async () => {
  isFetching.value = true;

  // fetch user profile
  await userStore.fetchUserProfile();
  account.value = { ...userStore.profile };

  // if admin, fetch total projects
  if (account.value.account_type === "admin") {
    await activationStore.fetchActivations();
    totalProjects.value = activationStore.totalActivations;
  }

  isFetching.value = false;
});

const confirmWipeDB = async () => {
  try {
    showWipeModal.value = false; // close the modal
    await activationStore.wipeDB();
    toast.success("Database wiped successfully!");
    // refresh totalProjects
    totalProjects.value = activationStore.totalActivations;
  } catch (err) {
    toast.error("Failed to wipe DB");
    console.error("Error wiping DB:", err);
  }
};

// Methods
const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value;
};

const handleLogout = () => {
  authStore.logout();
  router.push({ name: "LogIn" });
};

const goBack = () => {
  if (route.query.tab && route.query.projectId) {
    router.push({
      name: "ProjectPage",
      params: { id: route.query.projectId },
      query: { tab: route.query.tab },
    });
  } else {
    router.go(-1);
  }
};

const goToLinkEmailPage = () => {
  router.push({ name: "LinkEmail" });
};

const editAccount = () => {
  router.push({ name: "AccountEditPage" });
};

const goToProjectPage = (projectId) => {
  router.push({
    name: "ProjectPage",
    params: { id: projectId },
    query: { from: ["account", 0] },
  });
};

const copyToClipboard = (text) => {
  if (navigator.clipboard) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  } else {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      toast.success("Copied to clipboard!");
    } catch (err) {
      console.error("Oops, unable to copy", err);
    }
    document.body.removeChild(textArea);
  }
};

/*const updatePassword = () => {
  console.log("Redirect to update password page");
};*/
</script>

<style scoped>
/* Scoped styles */
</style>
